import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { useState, useCallback, useEffect, useRef } from "react";
import { joinMeeting } from "../utils/meeting/joinMeeting";
import { leaveMeeting } from "../utils/meeting/leaveMeeting";
import { joinVideoRoom } from "../utils/api/joinVideoRoom";
import { stopChimeStreams, stopAllTrackedStreams, forceStopAllMediaStreams } from "../utils/stopChimeStreams";

import { useAppState } from "../providers/AppStateProvider";
import { useDevices } from "../providers/DevicesProvider";
import { DeviceLabels } from "amazon-chime-sdk-component-library-react";
import { useNavigate, useParams } from "react-router-dom";
import { logger } from "../utils/chimeInit";
import {
  useVideo,
  useVideoDispatch,
  SET_MEETING_ID,
  SET_ROOM_ID,
  CLEAR_MEETING_ID,
  CLEAR_ROOM_ID,
  SET_INITIALIZED,
  SET_ROOM_ACCESS,
  SET_VERIFYING_ROOM_ACCESS,
  SET_ROOM_ACCESS_ERROR,
  SET_USER_NAME,
  SET_ROOM_MEMBERS_DATA,
  SET_MEETING_DETAILS,
  SET_FETCHING_MEETING_DETAILS,
  SET_MEETING_DETAILS_ERROR,
  SET_CC_STATUS,
  generateUniqueUserName,
} from "../providers/VideoContext";

import {
  verifyVideoRoomAccess,
  VideoRoomAccessResponse,
} from "../utils/api/verifyVideoRoomAccess";
import { leaveVideoRoom } from "../utils/api/leaveVideoRoom";
import { sendVideoRoomHeartbeat } from "../utils/api/videoRoomHeartbeat";
import {
  getMeetingDetails,
  MeetingDetails,
} from "../utils/api/getMeetingDetails";
import { usePlatformStore } from "@platform/store";
import { videoEventBus } from "../videoEventBus";
import { useErrorAnalytics } from "./useErrorAnalytics";

interface UseCustomMeetingManagerReturn {
  meetingManager: ReturnType<typeof useMeetingManager>;
  isInitialized: boolean;
  error: string | null;
  meetingId: string | null;
  roomId: number | null;

  roomAccess: VideoRoomAccessResponse | null;
  isVerifyingRoomAccess: boolean;
  roomAccessError: string | null;
  userName: string | null;
  meetingDetails: MeetingDetails | null;
  isFetchingMeetingDetails: boolean;
  meetingDetailsError: string | null;
  joinMeeting: (meetingId?: number) => Promise<void>;
  startDevices: () => Promise<void>;
  leaveMeeting: () => Promise<void>;
  createNewMeeting: () => void;

  verifyRoomAccess: (roomId: number) => Promise<void>;
  fetchMeetingDetails: (meetingId: string) => Promise<void>;
  checkMeetingDetailsAndRedirect: (meetingId: string) => Promise<boolean>;
}

export const useCustomMeetingManager = (): UseCustomMeetingManagerReturn => {
  const meetingManager = useMeetingManager();

  const navigateRef = useRef(useNavigate());

  const { meetingId: urlMeetingId } = useParams();

  const [error, setError] = useState<string | null>(null);

  const {
    meetingId,
    roomId,
    isInitialized,
    roomAccess,
    isVerifyingRoomAccess,
    roomAccessError,
    userName,
    meetingDetails,
    isFetchingMeetingDetails,
    meetingDetailsError,
  } = useVideo();
  const dispatch = useVideoDispatch();
  const user = usePlatformStore((s) => s.user);

  const { handleError } = useErrorAnalytics();
  const {
    region,
    enableSimulcast,
    priorityBasedPolicy,
    keepLastFrameWhenPaused,
    isWebAudioEnabled,
    isEchoReductionEnabled,
    setJoinInfo,
    setRegion,
    setMeetingMode,
  } = useAppState();

  const devicesContext = useDevices();
  const {
    selectedAudioInputDevice,
    selectedVideoInputDevice,
    selectedAudioOutputDevice,
    listAndSelectDevices,
  } = devicesContext;

  // Refs for heartbeat and participant polling
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const participantPollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize meeting ID from URL
  useEffect(() => {
    if (urlMeetingId && !meetingId) {
      dispatch({
        type: SET_MEETING_ID,
        payload: { meetingId: urlMeetingId },
      });
    }
  }, [urlMeetingId, meetingId, dispatch]);

  // Check if we're already in a meeting
  useEffect(() => {
    const checkMeetingState = async () => {
      try {
        if (meetingManager && meetingManager.meetingSession) {
          dispatch({ type: SET_INITIALIZED, payload: { isInitialized: true } });
        }
      } catch (err) {
        const errorMessage = (err as Error).message;
        setError(errorMessage);
      }
    };

    checkMeetingState();
  }, [meetingManager, dispatch]);

  // Generate and set username when user data is available
  useEffect(() => {
    if (user && !userName) {
      const uniqueUserName = generateUniqueUserName(
        user.first_name,
        user.last_name,
        user.id
      );

      dispatch({ type: SET_USER_NAME, payload: { userName: uniqueUserName } });
    }
  }, [user, userName, dispatch]);

  // Heartbeat function
  const sendHeartbeat = useCallback(async () => {
    if (!roomId || !meetingId || !userName) return;

    try {
      const response = await sendVideoRoomHeartbeat(roomId, meetingId, userName, user);
      // Update cc_status from heartbeat response
      if (response.cc_status) {
        dispatch({ type: SET_CC_STATUS, payload: { ccStatus: response.cc_status } });
      }
    } catch (err) {
      console.error('Heartbeat error:', err);
    }
  }, [roomId, meetingId, userName, user, dispatch]);

  // Start heartbeat when in a room
  useEffect(() => {
    if (roomId && isInitialized) {
      // Send initial heartbeat
      sendHeartbeat();

      // Set up heartbeat interval (every 30 seconds)
      heartbeatIntervalRef.current = setInterval(() => sendHeartbeat(), 30000);

      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      };
    }
  }, [roomId, isInitialized, sendHeartbeat]);

  // Poll for active users
  const pollActiveUsers = useCallback(async () => {
    if (!roomId) return;

    try {
      // const data = await getVideoRoomActiveUsers(roomId);
      // You can dispatch this to a context if you want to track participants
      // If no active users and we're not the creator, consider leaving
      // @todo: timeout room
    } catch (err) {
      logger.error(`Error polling active users: ${err}`);
    }
  }, [roomId]);

  // Start active users polling when in a room
  useEffect(() => {
    if (roomId && isInitialized) {
      // Poll immediately
      pollActiveUsers();

      // Set up polling interval (every 10 seconds)
      participantPollIntervalRef.current = setInterval(
        () => pollActiveUsers(),
        10000
      );

      return () => {
        if (participantPollIntervalRef.current) {
          clearInterval(participantPollIntervalRef.current);
          participantPollIntervalRef.current = null;
        }
      };
    }
  }, [roomId, isInitialized, pollActiveUsers]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (participantPollIntervalRef.current) {
        clearInterval(participantPollIntervalRef.current);
      }
    };
  }, []);

  const generateRandomMeetingId = useCallback(() => {
    return `meeting-${Math.random().toString(36).substring(2, 8)}`;
  }, []);

  const createNewMeeting = useCallback(() => {
    const newMeetingId = generateRandomMeetingId();

    dispatch({ type: SET_MEETING_ID, payload: { meetingId: newMeetingId } });

    navigateRef.current(`/video/join/${newMeetingId}`);
  }, [generateRandomMeetingId, dispatch]);

  const verifyRoomAccess = useCallback(
    async (roomId: number) => {
      dispatch({
        type: SET_VERIFYING_ROOM_ACCESS,
        payload: { isVerifyingRoomAccess: true },
      });
      dispatch({
        type: SET_ROOM_ACCESS_ERROR,
        payload: { roomAccessError: null },
      });

      try {
        const accessData = await verifyVideoRoomAccess(roomId);

        // Check if user is already in the room
        if (accessData.is_already_in_room) {
          dispatch({
            type: SET_ROOM_ACCESS_ERROR,
            payload: { 
              roomAccessError: 'ALREADY_IN_ROOM',
              lastHeartbeat: accessData.last_heartbeat 
            },
          });
          dispatch({ type: SET_ROOM_ACCESS, payload: { roomAccess: null } });
          return;
        }

        dispatch({
          type: SET_ROOM_ACCESS,
          payload: { roomAccess: accessData },
        });

        // Set roomId in context for heartbeat tracking
        if (accessData.room_id) {
          dispatch({
            type: SET_ROOM_ID,
            payload: { roomId: accessData.room_id },
          });
        }

        // Store room members data if available
        if (accessData.room_members) {
          dispatch({
            type: SET_ROOM_MEMBERS_DATA,
            payload: { roomMembersData: accessData.room_members },
          });
        }
      } catch (err) {
        const errorMessage = (err as Error).message;

        dispatch({
          type: SET_ROOM_ACCESS_ERROR,
          payload: { roomAccessError: errorMessage },
        });

        dispatch({ type: SET_ROOM_ACCESS, payload: { roomAccess: null } });
      } finally {
        dispatch({
          type: SET_VERIFYING_ROOM_ACCESS,
          payload: { isVerifyingRoomAccess: false },
        });
      }
    },
    [dispatch]
  );

  const fetchMeetingDetails = useCallback(
    async (meetingId: string) => {
      dispatch({
        type: SET_FETCHING_MEETING_DETAILS,
        payload: { isFetchingMeetingDetails: true },
      });
      dispatch({
        type: SET_MEETING_DETAILS_ERROR,
        payload: { meetingDetailsError: null },
      });

      try {
        const meetingData = await getMeetingDetails(meetingId);
        dispatch({
          type: SET_MEETING_DETAILS,
          payload: { meetingDetails: meetingData },
        });

        // Store room members data if available (now includes profile data from backend)
        if (meetingData.participants) {
          const roomMembersData = meetingData.participants.map(
            (participant) => ({
              user_id: participant.user.id,
              email: participant.user.email,
              first_name: participant.user.first_name,
              last_name: participant.user.last_name,
              pronouns: participant.user.pronouns,
              profile_photo: participant.user.profile_photo,
              role: participant.role.name,
              is_creator: false, // Would need to check against created_by
            })
          );
          dispatch({
            type: SET_ROOM_MEMBERS_DATA,
            payload: { roomMembersData },
          });
        }
      } catch (err) {
        const errorMessage = (err as Error).message;

        dispatch({
          type: SET_MEETING_DETAILS_ERROR,
          payload: { meetingDetailsError: errorMessage },
        });

        dispatch({
          type: SET_MEETING_DETAILS,
          payload: { meetingDetails: null },
        });
      } finally {
        dispatch({
          type: SET_FETCHING_MEETING_DETAILS,
          payload: { isFetchingMeetingDetails: false },
        });
      }
    },
    [dispatch]
  );

  const checkMeetingDetailsAndRedirect = useCallback(
    async (meetingId: string) => {
      // If meeting details don't exist, redirect to join page
      if (!meetingDetails) {
        navigateRef.current(`/video/join/${meetingId}`);

        return false;
      }
      return true;
    },
    [meetingDetails, navigateRef]
  );

  const startDevices = useCallback(async () => {
    try {
      setError(null);

      console.log('startDevices called - enumerating devices only');

      // Only enumerate and select devices, don't start them yet (Chime SDK devices can only be started after joining)
      try {
        await listAndSelectDevices(DeviceLabels.AudioAndVideo);
        console.log('listAndSelectDevices completed');
      } catch (deviceError) {
        // If device enumeration fails (e.g., no devices available), continue without devices
        console.warn("No devices available or device access denied:", deviceError);
      }

      // Check what the device provider has after listAndSelectDevices
      console.log('Device provider state after listAndSelectDevices:', {
        selectedAudioInputDevice,
        selectedVideoInputDevice,
        selectedAudioOutputDevice
      });

      console.log('Devices enumerated and selected, will start them after joining meeting');
    } catch (err) {
      const errorMessage = (err as Error).message;

      setError(errorMessage);

      handleError("device_start_failed");

      throw err;
    }
  }, [
    listAndSelectDevices,
  ]);

  const handleJoinMeeting = useCallback(
    async (meetingId?: number) => {
      try {
        setError(null);

        console.log('Device selections before joining:', {
          audioInput: selectedAudioInputDevice,
          videoInput: selectedVideoInputDevice,
          audioOutput: selectedAudioOutputDevice
        });

        // Start devices first and wait for it to complete
        await startDevices();

        let finalMeetingId: string;

        // Direct meeting joining (using meeting details)
        if (!meetingDetails) {
          throw new Error("No meeting details available");
        }

        // Find the first available video room for this meeting
        const videoRoom = meetingDetails.video_rooms?.[0];
        if (!videoRoom) {
          throw new Error("No video room available for this meeting");
        }

        // Call the join_video_room API to get Chime credentials
        const joinResponse = await joinVideoRoom({
          meeting_id: meetingDetails.id,
          region,
          echo_reduction: isEchoReductionEnabled,
        }, user);

        finalMeetingId = joinResponse.chime_meeting_id;

        // Now join the Chime meeting with the credentials
        await joinMeeting({
          meetingId: parseInt(finalMeetingId, 10),
          joinInfo: joinResponse.join_info,
          region,
          enableSimulcast,
          priorityBasedPolicy,
          keepLastFrameWhenPaused,
          isWebAudioEnabled,
          isEchoReductionEnabled,
          meetingManager,
          setJoinInfo,
          setRegion,
          setMeetingMode,
        });

        // Ensure devices are started after joining using the current selections
        try {
          console.log('Setting devices after joining meeting...');
          
          // Get fresh device selections from the context
          const currentAudioInput = devicesContext.selectedAudioInputDevice;
          const currentVideoInput = devicesContext.selectedVideoInputDevice;
          const currentAudioOutput = devicesContext.selectedAudioOutputDevice;
          
          console.log('Current device selections:', {
            audioInput: currentAudioInput,
            videoInput: currentVideoInput,
            audioOutput: currentAudioOutput
          });
          
          // Add a small delay to ensure Chime SDK is fully ready
          await new Promise(resolve => setTimeout(resolve, 500));
          
          if (currentAudioInput) {
            await meetingManager.startAudioInputDevice(currentAudioInput);
            console.log('Set audio input device after joining:', currentAudioInput);
          }

          if (currentVideoInput) {
            await meetingManager.startVideoInputDevice(currentVideoInput);
            console.log('Set video input device after joining:', currentVideoInput);
          }

          if (currentAudioOutput) {
            await meetingManager.startAudioOutputDevice(currentAudioOutput);
            console.log('Set audio output device after joining:', currentAudioOutput);
          }
        } catch (deviceError) {
          // If device starting fails, log but don't prevent joining
          console.warn("Some devices could not be started:", deviceError);
        }

        // Update the meeting ID in context if it changed
        if (finalMeetingId !== meetingId?.toString()) {
          dispatch({
            type: SET_MEETING_ID,
            payload: { meetingId: finalMeetingId },
          });
        }

        // Set roomId in context for heartbeat tracking
        if (joinResponse.room_id) {
          dispatch({
            type: SET_ROOM_ID,
            payload: { roomId: joinResponse.room_id },
          });
        }


        dispatch({ type: SET_INITIALIZED, payload: { isInitialized: true } });

        // Use the finalMeetingId directly since context might not be updated yet
        if (finalMeetingId && user?.id) {
          videoEventBus.emitEvent("videochat:joined", {
            meetingId: finalMeetingId,
          });
        }

        // Navigate to the meeting page
        // Navigate to meeting page with meeting ID
        if (meetingDetails) {
          navigateRef.current(`/video/meeting/${meetingDetails.id}`);
        }
      } catch (err) {
        const errorMessage = (err as Error).message;

        setError(errorMessage);

        handleError("meeting_join_failed");

        dispatch({ type: SET_INITIALIZED, payload: { isInitialized: false } });

        throw err;
      }
    },
    [
      meetingId,
      userName,
      region,
      enableSimulcast,
      priorityBasedPolicy,
      keepLastFrameWhenPaused,
      isWebAudioEnabled,
      isEchoReductionEnabled,
      meetingManager,
      setJoinInfo,
      setRegion,
      setMeetingMode,
      startDevices,
      selectedAudioInputDevice,
      selectedVideoInputDevice,
      selectedAudioOutputDevice,
      dispatch,
      meetingDetails,
    ]
  );

  const handleLeaveMeeting = useCallback(async () => {
    try {
      setError(null);

      // First, stop all tracked streams (most reliable)
      stopAllTrackedStreams();
      
      // Then stop Chime SDK streams properly
      await stopChimeStreams(meetingManager);
      
      // Finally force stop any remaining media streams
      forceStopAllMediaStreams();

      // Clear intervals first
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      if (participantPollIntervalRef.current) {
        clearInterval(participantPollIntervalRef.current);
        participantPollIntervalRef.current = null;
      }

      // If we have a room ID, call the leave room API
      if (roomId) {
        try {
          await leaveVideoRoom({ room_id: roomId });
        } catch (apiError) {}
      }

      // Always clean up the Chime meeting
      if (meetingId) {
        await leaveMeeting({
          meetingId,
          meetingManager,
          setMeetingMode,
        });
      }

      // Track leave event
      if (meetingId) {
        videoEventBus.emitEvent("videochat:left", { meetingId });
      }

      // Clear all meeting state
      dispatch({ type: CLEAR_MEETING_ID });

      dispatch({ type: CLEAR_ROOM_ID });
    } catch (err) {
      const errorMessage = (err as Error).message;

      setError(errorMessage);

      handleError("meeting_leave_failed");

      // Still clear the state even if there's an error
      dispatch({ type: CLEAR_MEETING_ID });

      dispatch({ type: CLEAR_ROOM_ID });

      throw err;
    }
  }, [meetingId, roomId, meetingManager, setMeetingMode, dispatch]);

  return {
    meetingManager,
    isInitialized,
    error,
    meetingId,
    roomId,
    roomAccess,
    isVerifyingRoomAccess,
    roomAccessError,
    userName,
    meetingDetails,
    isFetchingMeetingDetails,
    meetingDetailsError,
    joinMeeting: handleJoinMeeting,
    startDevices,
    leaveMeeting: handleLeaveMeeting,
    createNewMeeting,
    verifyRoomAccess,
    fetchMeetingDetails,
    checkMeetingDetailsAndRedirect,
  };
};
